import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ForgotPasswordForm } from "@/components/auth/forgot-password-form";
import { PasswordLoginForm } from "@/components/auth/password-login-form";
import { OnboardingFlow } from "@/components/onboarding/onboarding-flow";
import { api } from "@/lib/api";
import type { AuthUserPayload } from "@/lib/auth-api";
import { hydrateAuthUser } from "@/lib/auth-api";
import { safeInternalRedirect } from "@/lib/redirect";
import { cn } from "@/lib/utils";

interface LoginFormProps extends React.ComponentProps<"div"> {
  /** In-app continuation to resume after login (e.g. an interrupted OAuth entry). */
  redirectTo?: string;
}

export function LoginForm({ className, redirectTo, ...props }: LoginFormProps) {
  const navigate = useNavigate();

  const [step, setStep] = useState<"auth" | "onboarding">("auth");

  // ADP-167: password login replaces the OTP flow. `authView` toggles between
  // the login form and the forgot-password (set password) form within step 1.
  const [authView, setAuthView] = useState<"login" | "forgot">("login");
  const [forgotPrefillEmail, setForgotPrefillEmail] = useState("");

  async function afterLogin(user: AuthUserPayload) {
    hydrateAuthUser(user);

    // First login without any connected shop → guide into Amazon authorization.
    if (user.shops.length === 0) {
      setStep("onboarding");
      return;
    }
    const continuation = safeInternalRedirect(redirectTo, window.location.origin);
    if (continuation) {
      // A continuation may point at an API OAuth entry point, which is not a
      // router route — leave the SPA rather than navigating within it.
      window.location.assign(continuation);
      return;
    }
    await navigate({ to: "/" });
  }

  async function handleLogin(email: string, password: string) {
    const response = await api.post("auth/login", { json: { email, password } }).json<{
      success: true;
      data: { user: AuthUserPayload };
    }>();
    await afterLogin(response.data.user);
  }

  async function handleForgotSendCode(email: string) {
    await api.post("auth/password/forgot", { json: { email } }).json();
  }

  async function handlePasswordReset(email: string, code: string, newPassword: string) {
    await api.post("auth/password/reset", { json: { email, code, newPassword } }).json();
    // Reset revokes all sessions — log in with the fresh credentials.
    await handleLogin(email, newPassword);
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      {step === "auth" ? (
        authView === "login" ? (
          <PasswordLoginForm
            onLogin={handleLogin}
            onForgotPassword={(prefillEmail) => {
              setForgotPrefillEmail(prefillEmail);
              setAuthView("forgot");
            }}
          />
        ) : (
          <ForgotPasswordForm
            prefillEmail={forgotPrefillEmail}
            onSendCode={handleForgotSendCode}
            onReset={handlePasswordReset}
            onBack={() => setAuthView("login")}
          />
        )
      ) : (
        <OnboardingFlow />
      )}
    </div>
  );
}
